/** Contextual image operation / case-stage panel alongside the Study viewer. */

import { useEffect } from "react";
import { Play, Square } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
  Select,
} from "@/components/ui/primitives";
import { formatDuration } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { stageParams } from "@/pipeline/params";
import { ParamTable, paramsCaption } from "@/pipeline/ParamTable";
import { jobElapsedSec, weightedProgressPct } from "@/pipeline/progress";
import { openProtocolStep } from "@/pipeline/protocol";
import { usePipelineSession } from "@/pipeline/session";
import { JOB_LABEL, JobGlyph } from "@/pipeline/status";
import { ensureStageTimings, useStageTimings } from "@/pipeline/timings";
import { useNow } from "@/pipeline/useNow";
import { appActions, useCurrentCase, useJob, useJobRunning, useSelectedNodeId, useSystemStatus } from "@/store/appStore";
import { toolById, toolsForTab, type ToolTabId } from "@/tools/catalog";
import { parameterIssue, toolRunIssue } from "@/tools/validation";
import { formatHeader } from "@/tools/header";
import { useToolSession } from "@/tools/session";
import { findNode, nodeAbsPath, useViewerStore } from "@/viewer/viewerStore";

ensureStageTimings();

export function ToolWorkspace({ tab }: { tab: ToolTabId }) {
  const selectedId = useToolSession((s) => s.selectedId);
  const selectTabDefault = useToolSession((s) => s.selectTabDefault);
  const runSelected = useToolSession((s) => s.runSelected);
  const notice = useToolSession((s) => s.notice);
  const setNotice = useToolSession((s) => s.setNotice);
  const header = useToolSession((s) => s.header);
  const busy = useToolSession((s) => s.busy);
  const fieldValues = useToolSession((s) => s.fieldValues);
  const setField = useToolSession((s) => s.setField);
  const referenceNodeId = useToolSession((s) => s.referenceNodeId);
  const setReferenceNodeId = useToolSession((s) => s.setReferenceNodeId);
  const opResult = useToolSession((s) => s.opResult);
  const currentCase = useCurrentCase();
  const selectedNodeId = useSelectedNodeId();
  const job = useJob();
  const running = useJobRunning();
  const system = useSystemStatus();
  const stop = usePipelineSession((s) => s.stop);
  const pipelineNotice = usePipelineSession((s) => s.notice);
  const timings = useStageTimings((s) => s.timings);
  const now = useNow(1000, running);

  useEffect(() => {
    selectTabDefault(tab);
  }, [tab, selectTabDefault]);

  const tool = toolById(selectedId ?? "");
  const shown = tool?.tab === tab ? tool : toolsForTab(tab).find((t) => t.status === "ready") ?? toolsForTab(tab)[0];
  const volumes = currentCase ? [...currentCase.images, ...currentCase.masks, ...currentCase.atlases] : [];
  const selected = findNode(currentCase, selectedNodeId);
  const inputNode = volumes.find((n) => n.id === selected?.id) ?? null;
  const inputPath = nodeAbsPath(currentCase, inputNode);
  const groups = shown?.stage ? stageParams(shown.stage.modality, shown.stage.id) : [];
  const issue = toolRunIssue(shown, {
    hasCase: Boolean(currentCase), hasInput: Boolean(inputPath),
    hasReference: volumes.some((n) => n.id === referenceNodeId), busy, jobRunning: running,
    engineReady: system?.engine.ready === true, pipelineKind: currentCase?.pipelineKind,
  }) ?? (shown ? parameterIssue(shown, fieldValues) : null);


  return (
    <div className="tool-workspace h-full overflow-auto bg-ads-bg p-2">
      {notice ? (
        <div
          className={cn(
            "mb-3 flex items-start justify-between gap-3 rounded border px-3 py-2 text-xs",
            notice.tone === "error"
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-ads-accent-border bg-ads-accent-soft text-ads-text",
          )}
        >
          <span>
            {notice.message}
            {notice.detail ? <span className="mt-1 block font-mono text-[11px] opacity-70">{notice.detail}</span> : null}
          </span>
          <button type="button" className="underline" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-3">
        <Card className="col-span-12 ">
          <CardHeader>
            <CardTitle>{shown?.stage ? "Case input" : "Input image"}</CardTitle>
            {shown ? (
              <Badge tone={shown.status === "ready" ? "green" : "amber"}>{shown.label}</Badge>
            ) : null}
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {!currentCase ? (
              <EmptyState title="No case open" hint="Open a study from Home or File → Open Subject." />
            ) : volumes.length === 0 ? (
              <EmptyState title="No volumes" hint="The case has no NIfTI nodes yet." />
            ) : (
              <>
                {shown?.stage ? <p className="rounded bg-ads-accent-soft p-2 text-xs">This is a protocol step, not an image tool. It uses the case inputs on the Pipeline tab, not the volume selected here.</p> : null}
                <label className="block text-[11px] text-neutral-500">{shown?.stage ? "Viewing image (not stage input)" : "Selected image"}</label>
                <Select
                  value={inputNode?.id ?? ""}
                  onChange={(event) => appActions.setSelectedNodeId(event.target.value)}
                >
                  <option value="">Select an image…</option>
                  {volumes.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.label} ({node.origin})
                    </option>
                  ))}
                </Select>
                {shown?.op?.startsWith("resample_") ? <p className="rounded bg-ads-accent-soft p-2 text-xs">Interpolation: {currentCase && [...currentCase.masks, ...currentCase.atlases].some((n) => n.id === inputNode?.id) ? "Nearest neighbour (label image)" : "Linear (intensity image)"}. Resampling changes the grid; it does not align anatomy.</p> : null}
                {inputPath ? (
                  <p className="break-all font-mono text-[11px] text-neutral-400">{inputPath}</p>
                ) : null}
                <p className="text-xs text-neutral-600">{shown?.description}</p>
              </>
            )}
          </CardBody>
        </Card>

        <Card className="col-span-12 ">
          <CardHeader>
            <CardTitle>Parameters</CardTitle>
          </CardHeader>
          <CardBody>
            {!shown ? (
              <EmptyState title="Select a tool" hint="Use the Tool Library on the left." />
            ) : shown.status === "future" ? (
              <p className="text-xs text-amber-800">{shown.comingSoon}</p>
            ) : (
              <div className="space-y-3 text-xs">
                {shown.needsReference ? (
                  <div>
                    <label className="mb-1 block text-[11px] text-neutral-500">Second volume / reference</label>
                    <Select
                      value={referenceNodeId ?? ""}
                      onChange={(event) => setReferenceNodeId(event.target.value || null)}
                    >
                      <option value="">Select…</option>
                      {volumes.map((node) => (
                        <option key={node.id} value={node.id}>
                          {node.label} ({node.origin})
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}
                {shown.fields?.length ? (
                  shown.fields.map((field) => {
                    const value = fieldValues[`${shown.id}.${field.id}`] ?? field.value;
                    return (
                      <div key={field.id} className="flex items-center justify-between gap-3">
                        <label className="text-neutral-500">{field.label}</label>
                        {field.kind === "select" && field.options ? (
                          <Select
                            className="w-40"
                            value={value}
                            onChange={(event) => setField(shown.id, field.id, event.target.value)}
                          >
                            {field.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <input
                            className="w-40 rounded border border-neutral-200 px-2 py-1 font-mono"
                            value={value}
                            onChange={(event) => setField(shown.id, field.id, event.target.value)}
                          />
                        )}
                      </div>
                    );
                  })
                ) : groups.length === 0 && !shown.op ? (
                  <p className="text-neutral-500">
                    This tool has no container parameters. It inspects or opens an existing surface.
                  </p>
                ) : groups.length > 0 ? (
                  <>
                    <ParamTable groups={groups} subjectId={currentCase?.subjectId} openFirst={1} />
                    <p className="text-[10px] leading-snug text-neutral-400">{paramsCaption(groups)}</p>
                  </>
                ) : shown.op ? (
                  <p className="text-neutral-500">
                    Uses the default settings for this operation.
                  </p>
                ) : null}
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="col-span-12 ">
          <CardHeader>
            <CardTitle>Output & inspection</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-xs">
            {header && (shown?.inspect === "header" || shown?.id.startsWith("prep.")) ? (
              <div className="grid gap-1 sm:grid-cols-2">
                {formatHeader(
                  header,
                  shown?.id === "prep.affineMatrix" ? "affine" : shown?.id === "prep.qform" ? "qform" : "header",
                ).map((row) => (
                  <Row key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            ) : opResult ? (
              <div className="space-y-1">
                <Row label="Result" value={opResult.message} />
                {opResult.outputPath ? <Row label="Output" value={opResult.outputPath} /> : null}
                {Object.entries(opResult.stats).map(([key, value]) => (
                  <Row key={key} label={key} value={Array.isArray(value) ? value.join(", ") : String(value)} />
                ))}
              </div>
            ) : (
              <p className="text-neutral-500">
                The image remains visible in the viewer. Run creates an output in the Study Explorer; inspect it before using it in further analysis.
              </p>
            )}
            {job ? (
              <div className="space-y-1 border-t border-neutral-100 pt-2">
                <div className="flex items-center gap-1.5 text-neutral-600">
                  <JobGlyph state={job.state} />
                  {JOB_LABEL[job.state]}
                </div>
                <Progress value={weightedProgressPct(job, timings, now, job?.deviceClass)} />
                <div>Elapsed {formatDuration(jobElapsedSec(job, now))}</div>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card className="col-span-12 order-first sticky top-0 z-10">
          <CardHeader>
            <CardTitle>Execution</CardTitle>
            {shown ? <span className="text-[11px] text-neutral-400">{shown.engine}</span> : null}
          </CardHeader>
          <CardBody className="space-y-2">
            {shown?.stage && pipelineNotice ? <p role="status" className="text-xs text-amber-800">{pipelineNotice.message}</p> : null}
            {issue ? <p role="status" className="text-xs text-amber-800">{issue}</p> : null}
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                variant="primary"
                disabled={shown?.stage ? false : Boolean(issue)}
                title={shown?.stage ? "Opens this step on the Pipeline protocol" : issue ?? undefined}
                onClick={() => {
                  if (shown?.stage) {
                    openProtocolStep(shown.stage.modality, shown.stage.id);
                    return;
                  }
                  void runSelected();
                }}
              >
                <Play className="h-3.5 w-3.5" />
                {shown?.stage
                  ? "Open in protocol"
                  : shown?.op
                    ? "Run on image"
                    : shown?.inspect === "header"
                      ? "Read header"
                      : "Open"}
              </Button>
              <Button type="button" variant="danger" disabled={!running} onClick={() => void stop()}>
                <Square className="h-3.5 w-3.5" />
                Stop
              </Button>
              <Button
                type="button"
                disabled={!inputNode}
                onClick={() => {
                  if (inputNode) {
                    useViewerStore.getState().setBaseNode(inputNode.id);
                    appActions.setSelectedNodeId(inputNode.id);
                    appActions.setActiveTab("view");
                  }
                }}
              >
                Show selected image
              </Button>
            </div>
            {shown?.stage ? (
              <p className="text-[11px] text-neutral-500">
                Opens Pipeline and highlights <span className="font-mono">{shown.stage.modality}:{shown.stage.id}</span>.
                Run from there so the protocol stays the single runner.
              </p>
            ) : shown?.op ? (
              <p className="text-[11px] text-neutral-500">Creates a new output image; previous outputs are preserved.</p>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-neutral-500">{label}</span>
      <span className="whitespace-pre-wrap break-all font-mono text-neutral-800" title={value}>
        {value}
      </span>
    </div>
  );
}

export function PreprocessTab() {
  return <ToolWorkspace tab="preprocess" />;
}
export function RegistrationTab() {
  return <ToolWorkspace tab="registration" />;
}
export function SegmentationTab() {
  return <ToolWorkspace tab="segmentation" />;
}
export function PerfusionTab() {
  return <ToolWorkspace tab="perfusion" />;
}
export function QuantificationTab() {
  return <ToolWorkspace tab="quantification" />;
}
